import { motion } from "framer-motion";
import { CATEGORIES } from "../../data/menu";

const reveal = {
  hidden: { y: 40, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 1, ease: [0.19, 1, 0.22, 1] } },
};

const MenuRow = ({ item, index }) => (
  <motion.li
    variants={reveal}
    data-testid={`menu-item-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
    className="group grid grid-cols-12 gap-4 md:gap-8 py-7 md:py-8 border-t border-bone/10 transition-colors duration-500 hover:bg-bone/[0.015]"
  >
    <div className="col-span-1 text-[11px] font-mono text-bone/30 pt-1">
      {String(index + 1).padStart(2, "0")}
    </div>
    <div className="col-span-8 md:col-span-6">
      <h3 className="font-serif text-2xl md:text-4xl leading-none text-bone group-hover:text-gold transition-colors duration-500">
        {item.name}
      </h3>
      <p className="mt-3 text-xs md:text-[13px] text-bone/50 max-w-md font-light leading-relaxed">
        {item.note}
      </p>
    </div>
    <div className="hidden md:block md:col-span-3 text-[11px] uppercase tracking-widest-2 text-bone/50 pt-2">
      {item.origin}
    </div>
    <div className="col-span-3 md:col-span-2 text-right font-serif text-2xl md:text-3xl text-bone tabular-nums pt-1">
      <span className="text-gold/70 text-base align-top mr-1">$</span>
      {item.price}
    </div>
  </motion.li>
);

const CategoryBlock = ({ cat, isEven }) => {
  return (
    <section
      id={cat.id}
      data-testid={`category-${cat.id}`}
      className="relative py-24 md:py-40 border-t border-bone/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="grid grid-cols-12 gap-4 md:gap-8 mb-16 md:mb-24"
        >
          <div className="col-span-12 md:col-span-6 flex items-baseline gap-6 md:gap-10">
            <span className="font-serif text-4xl md:text-6xl text-bone/15 leading-none">
              {cat.number}
            </span>
            <h2 className="font-serif font-light text-[13vw] md:text-[8vw] leading-[0.85] tracking-tightest text-bone">
              {cat.title.split(" ").map((w, i) => (
                <span key={i} className={i === 1 ? "italic text-gold" : ""}>
                  {w}
                  {i < cat.title.split(" ").length - 1 ? " " : ""}
                </span>
              ))}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 flex md:justify-end items-end">
            <p className="font-serif italic text-lg md:text-xl text-bone/60 text-left md:text-right max-w-xs">
              — {cat.caption}
            </p>
          </div>
        </motion.div>

        {/* Grid: hero photo + list */}
        <div className={`grid grid-cols-12 gap-6 md:gap-16 ${isEven ? "" : "md:[direction:rtl]"}`}>
          {cat.hero && (
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
              className="col-span-12 md:col-span-4 [direction:ltr]"
            >
              <div className="relative overflow-hidden aspect-[3/4] bg-ink-soft">
                <motion.img
                  src={cat.hero}
                  alt={cat.title}
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.15 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 1.8, ease: [0.19, 1, 0.22, 1] }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink/50" />
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-[10px] uppercase tracking-widest-2 text-bone/70">
                  <span>Fig. {cat.number}</span>
                  <span className="text-right">{cat.title}</span>
                </div>
              </div>
            </motion.div>
          )}

          <motion.ol
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            transition={{ staggerChildren: 0.08 }}
            className={`col-span-12 [direction:ltr] ${cat.hero ? "md:col-span-8" : "md:col-span-10 md:col-start-2"}`}
            data-testid={`items-list-${cat.id}`}
          >
            {cat.items.map((item, i) => (
              <MenuRow key={item.name} item={item} index={i} />
            ))}
            <li className="border-t border-bone/10" aria-hidden />
          </motion.ol>
        </div>
      </div>
    </section>
  );
};

export const MenuSection = () => {
  return (
    <div data-testid="menu-root">
      {CATEGORIES.map((cat, i) => (
        <CategoryBlock key={cat.id} cat={cat} isEven={i % 2 === 0} />
      ))}
    </div>
  );
};
